import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuditLog } from '../../../../services/log.service';

export interface AuditDetailDialogData {
  log: AuditLog;
}

export interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

@Component({
  selector: 'app-audit-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './audit-detail-dialog.component.html',
  styleUrls: ['./audit-detail-dialog.component.scss'],
})
export class AuditDetailDialogComponent {
  dialogRef = inject<MatDialogRef<AuditDetailDialogComponent>>(MatDialogRef);
  data = inject<AuditDetailDialogData>(MAT_DIALOG_DATA);

  fieldChanges: FieldChange[] = [];

  constructor() {
    this.parseChanges();
  }

  private parseChanges(): void {
    const oldValues = this.parseJson(this.data.log.oldValues);
    const newValues = this.parseJson(this.data.log.newValues);

    // Get all unique fields from both old and new values
    const allFields = new Set<string>([
      ...Object.keys(oldValues),
      ...Object.keys(newValues),
    ]);

    // Build the field changes array
    this.fieldChanges = Array.from(allFields).map((field) => ({
      field,
      oldValue: this.formatValue(oldValues[field]),
      newValue: this.formatValue(newValues[field]),
    }));

    // Sort by field name
    this.fieldChanges.sort((a, b) => a.field.localeCompare(b.field));
  }

  private parseJson(jsonString?: string): Record<string, unknown> {
    if (!jsonString) return {};
    try {
      return JSON.parse(jsonString);
    } catch {
      return {};
    }
  }

  private formatValue(value: unknown): string | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  getActionClass(typeName: string): string {
    switch (typeName?.toLowerCase()) {
      case 'create':
        return 'action-create';
      case 'read':
        return 'action-read';
      case 'update':
        return 'action-update';
      case 'delete':
        return 'action-delete';
      default:
        return '';
    }
  }

  formatTimestamp(timestamp: string): string {
    // Backend sends DateTimeOffset with timezone information
    const date = new Date(timestamp);
    const formatted = date.toLocaleString();
    // Extract short timezone abbreviation (e.g., EST, PST)
    const timezone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    return `${formatted} (${timezone})`;
  }

  formatFieldName(field: string): string {
    // Convert camelCase or PascalCase to Title Case with spaces
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  hasChanges(): boolean {
    return this.fieldChanges.length > 0;
  }

  isValueChanged(change: FieldChange): boolean {
    return change.oldValue !== change.newValue;
  }

  getTimezoneTooltip(): string | null {
    const offsetMinutes = new Date().getTimezoneOffset();
    if (offsetMinutes === 0) return null;
    // Convert to minutes offset from UTC (getTimezoneOffset is opposite sign)
    const totalMinutesFromUtc = -offsetMinutes;
    const sign = totalMinutesFromUtc >= 0 ? '+' : '-';
    const absMinutes = Math.abs(totalMinutesFromUtc);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    const offsetString =
      minutes === 0
        ? `${sign}${hours}`
        : `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
    return `All times displayed here are in UTC format and will differ from your local timezone by UTC${offsetString}.`;
  }
}
